import { showLoading, hideLoading } from "./utils";
import { doneMessage, errorMessage } from "./notifications";

function handlerStatusResponse(response, showAlert) {

  // Detectar redirección automática al login (Shield)
  if (response.redirected && response.url) {
    const finalUrl = new URL(response.url);
    if (finalUrl.pathname.includes('/login')) {
      window.location.href = response.url;
      return;
    }
  }

  // Interceptor de sesión expirada (401)
  if (response.status === 401) {
    const contentType = response.headers.get("Content-Type");
    if (contentType && contentType.includes("application/json")) {
      return response.json().then(data => {
        if (data.session_expired) {
          window.location.href = data.redirect ?? "/login?reason=session_expired";
          return;
        }
        errorMessage(data.message ?? "No autorizado");
      }).catch(() => {
        window.location.href = "/login?reason=session_expired";
      });
    } else {
      window.location.href = "/login?reason=session_expired";
      return;
    }
  }

  // Sin permisos (403)
  if (response.status === 403) {
    errorMessage("No tiene permisos para realizar esta acción");
    return null;
  }

  // Manejo de redirección 303 (fallback)
  if (response.status === 303) {
    const location = response.headers.get("Location");
    window.location.href = location ?? "/login?reason=session_expired";
    return;
  }

  // Success (200-299)
  if (response.status >= 200 && response.status <= 299) {
    const contentType = response.headers.get("Content-Type");
    if (contentType && contentType.includes("application/json")) {
      if (showAlert) {
        doneMessage("Procesado correctamente");
      }
      return response.json();
    } else {
      errorMessage("Respuesta inesperada del servidor");
      return null;
    }
  }

  // Error de validación (422)
  if (response.status === 422) {
    errorMessage("Error de validación. Verifique la información ingresada.");
    const contentType = response.headers.get("Content-Type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }
    return null;
  }

  // Not Found (404)
  if (response.status === 404) {
    errorMessage("Recurso no encontrado");
    return null;
  }

  // Server Error (500+)
  if (response.status >= 500) {
    errorMessage("Error del servidor. Intente nuevamente más tarde.");
    return null;
  }

  // Otros errores
  const contentType = response.headers.get("Content-Type");
  if (contentType && contentType.includes("application/json")) {
    return response.json().then((data) => {
      errorMessage(data.message ?? "Ha ocurrido un error inesperado");
    }).catch(() => {
      errorMessage("Ha ocurrido un error inesperado");
    });
  } else {
    errorMessage("Ha ocurrido un error inesperado");
    return null;
  }
}

async function postData(url = "", data = {}, showAlert = true) {
  try {
    showLoading();

    const response = await fetch(url, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });

    hideLoading();
    return handlerStatusResponse(response, showAlert);
  } catch (error) {
    hideLoading();
    errorMessage("Error de conexión", error);
    return null;
  }
}

async function putData(url = "", id = "", data = {}, showAlert = true) {
  try {
    showLoading();
    const response = await fetch(`${url}/${id}`, {
      method: "PUT", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    hideLoading();
    return handlerStatusResponse(response, showAlert);
  } catch (error) {
    hideLoading();
    errorMessage("Error de conexión", error);
    return null;
  }
}

async function getData(url = "") {
  try {
    showLoading();
    const response = await fetch(url, {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
      },
      redirect: "follow",
    });

    hideLoading();
    return handlerStatusResponse(response, false);
  } catch (error) {
    hideLoading();
    errorMessage("Error de conexión", error);
    return null;
  }
}

async function deleteData(url = "", id = "") {
  try {
    showLoading();
    const response = await fetch(`${url}/${id}`, {
      method: "DELETE",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      redirect: "follow",
    });

    hideLoading();
    return handlerStatusResponse(response, false);
  } catch (error) {
    hideLoading();
    errorMessage("Error de conexión", error);
    return null;
  }
}

export { getData, postData, putData, deleteData };